import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Play, Pause, RotateCcw, Minus, Plus, Volume2, VolumeX } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/hooks/useFetch';

// Inline DinoSprite for Timer page - AQUAMARINE with WHITE eye
// Values: 0=empty, 1=dino color, 2=white (eye)
const DinoSprite = ({ frame, isJumping }) => {
  const pixels1 = [
    [0,0,0,0,0,0,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,2,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,0,0,0],
    [0,0,0,1,0,1,1,1,1,1,0,0],
    [0,0,1,1,1,1,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,1,1,0,0,0],
    [1,1,1,1,1,1,1,1,0,0,0,0],
    [0,1,1,1,1,1,1,1,0,0,0,0],
    [0,0,1,1,1,1,1,0,0,0,0,0],
    [0,0,0,1,1,0,1,1,0,0,0,0],
    [0,0,0,1,0,0,0,1,0,0,0,0],
    [0,0,0,1,1,0,0,0,0,0,0,0],
  ];
  
  const pixels2 = [
    [0,0,0,0,0,0,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,2,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,0,0,0],
    [0,0,0,1,0,1,1,1,1,1,0,0],
    [0,0,1,1,1,1,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,1,1,0,0,0],
    [1,1,1,1,1,1,1,1,0,0,0,0],
    [0,1,1,1,1,1,1,1,0,0,0,0],
    [0,0,1,1,1,1,1,0,0,0,0,0],
    [0,0,0,1,0,1,0,0,0,0,0,0],
    [0,0,0,1,0,1,0,0,0,0,0,0],
    [0,0,1,1,0,1,1,0,0,0,0,0],
  ];
  
  const pixels = frame === 0 ? pixels1 : pixels2;
  const colors = { 1: '#00E5CC', 2: '#FFFFFF' };
  
  return (
    <div 
      className="relative transition-transform duration-150" 
      style={{ 
        width: '48px', 
        height: '56px',
        transform: isJumping ? 'translateY(-50px)' : 'translateY(0)'
      }}
    >
      {pixels.map((row, y) => 
        row.map((pixel, x) => 
          pixel > 0 && (
            <div
              key={`${x}-${y}`}
              className="absolute"
              style={{
                left: `${x * 4}px`,
                top: `${y * 4}px`,
                width: '4px',
                height: '4px',
                backgroundColor: colors[pixel],
              }}
            />
          )
        )
      )}
    </div>
  );
};

// Inline LlamaSprite for Timer page - DEEP PURPLE
const LlamaSprite = () => {
  const pixels = [
    [0,0,0,1,0,1,0,0],
    [0,0,0,1,1,1,0,0],
    [0,0,0,0,1,0,0,0],
    [0,0,0,0,1,0,0,0],
    [0,0,0,0,1,0,0,0],
    [0,0,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,0],
    [0,1,1,1,1,1,1,0],
    [0,0,1,1,1,1,0,0],
    [0,0,1,0,0,1,0,0],
    [0,0,1,0,0,1,0,0],
    [0,0,1,0,0,1,0,0],
  ];
  
  return (
    <div className="relative" style={{ width: '32px', height: '48px' }}>
      {pixels.map((row, y) => 
        row.map((pixel, x) => 
          pixel === 1 && (
            <div
              key={`${x}-${y}`}
              className="absolute"
              style={{
                left: `${x * 4}px`,
                top: `${y * 4}px`,
                width: '4px',
                height: '4px',
                backgroundColor: '#7C3AED',
              }}
            />
          )
        )
      )}
    </div>
  );
};

const DinoLlamaTimerPage = () => {
  const { t, isSpanish } = useLanguage();
  const { isAuthenticated } = useAuth();
  // Default to 0/0 so the timer doesn't auto-suggest a 5-min session — the
  // user must explicitly pick a duration (presets or +/- adjusters) before
  // Start. This prevents accidental Start with the wrong duration.
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showEndMessages, setShowEndMessages] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0); // track started duration
  
  // Animation states
  const [frame, setFrame] = useState(0);
  const [llamaPos, setLlamaPos] = useState(100);
  const [isJumping, setIsJumping] = useState(false);

  // Rawr-on-complete preference. Persisted in localStorage so the user's
  // choice survives reloads. Defaults to ON — most users enjoy the little
  // reward sound. Mute via the speaker toggle next to the timer header.
  const [rawrMuted, setRawrMuted] = useState(() => {
    try { return localStorage.getItem('cca_timer_rawr_muted') === '1'; }
    catch { return false; }
  });
  // "Slower Llama 🐢" preference — when true, the llama moves at ~60% of
  // its normal walking speed so kids/users who like a gentler pace can
  // enjoy the animation without it whizzing past. Persisted in
  // localStorage just like the rawr-mute preference.
  const [slowerLlama, setSlowerLlama] = useState(() => {
    try { return localStorage.getItem('cca_timer_slower_llama') === '1'; }
    catch { return false; }
  });
  const rawrAudioRef = useRef(null);
  const triceratopsAudioRef = useRef(null);

  // Per-day session counter for the bonus "Triceratops growl" reward.
  // Stored as `cca_timer_sessions_YYYY-MM-DD = N`. Resets each new day.
  const todayKey = () => {
    const d = new Date();
    return `cca_timer_sessions_${d.toISOString().slice(0, 10)}`;
  };
  const [sessionsToday, setSessionsToday] = useState(() => {
    try { return parseInt(localStorage.getItem(todayKey()) || '0', 10) || 0; }
    catch { return 0; }
  });
  const [showXpFloat, setShowXpFloat] = useState(false);
  const [showTriceratopsToast, setShowTriceratopsToast] = useState(false);

  // Preload both audio files on mount so completion playback is instant
  // even on slow connections.
  useEffect(() => {
    try {
      const a = new Audio('/sounds/dino-rawr.mp3');
      a.preload = 'auto';
      a.volume = 0.6;
      rawrAudioRef.current = a;

      const b = new Audio('/sounds/triceratops-growl.mp3');
      b.preload = 'auto';
      b.volume = 0.7; // slightly louder — it's a rare bonus
      triceratopsAudioRef.current = b;
    } catch { /* ignore — audio unsupported */ }
  }, []);

  const playRawr = useCallback(() => {
    if (rawrMuted) return;
    // Increment the per-day session counter
    let next = 0;
    try {
      const key = todayKey();
      next = (parseInt(localStorage.getItem(key) || '0', 10) || 0) + 1;
      localStorage.setItem(key, String(next));
    } catch { /* ignore */ }
    setSessionsToday(next);

    // Show the floating "🔥 +1" animation regardless of which audio plays
    setShowXpFloat(true);
    setTimeout(() => setShowXpFloat(false), 1800);

    // Decide which sound to play — every 10th completion drops the
    // Triceratops growl. Otherwise the normal rawr.
    const isMilestone = next > 0 && next % 10 === 0;
    const audio = isMilestone ? triceratopsAudioRef.current : rawrAudioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      audio.play().catch(() => { /* autoplay blocked — ignore */ });
    } catch { /* ignore */ }

    if (isMilestone) {
      setShowTriceratopsToast(true);
      setTimeout(() => setShowTriceratopsToast(false), 4500);
    }
  }, [rawrMuted]);

  const toggleRawrMute = () => {
    setRawrMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem('cca_timer_rawr_muted', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const toggleSlowerLlama = () => {
    setSlowerLlama((prev) => {
      const next = !prev;
      try { localStorage.setItem('cca_timer_slower_llama', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  
  // Jump when llama is near
  useEffect(() => {
    if (isRunning && llamaPos <= 25 && llamaPos >= 15 && !isJumping) {
      setIsJumping(true);
      setTimeout(() => setIsJumping(false), 400);
    }
  }, [llamaPos, isJumping, isRunning]);
  
  // Animation loop
  useEffect(() => {
    if (!isRunning || showEndMessages) return;
    
    const frameInterval = setInterval(() => {
      setFrame(prev => (prev + 1) % 2);
    }, 100);
    
    const llamaInterval = setInterval(() => {
      setLlamaPos(prev => {
        if (prev <= -10) return 100;
        // Slower Llama mode → ~60% of normal walking speed.
        return prev - (slowerLlama ? 0.9 : 1.5);
      });
    }, 30);
    
    return () => {
      clearInterval(frameInterval);
      clearInterval(llamaInterval);
    };
  }, [isRunning, showEndMessages, slowerLlama]);
  
  // Timer countdown
  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setIsRunning(false);
          setShowEndMessages(true);
          // 🦖 Reward the user with a quick dino growl when they finish
          // a session. Honors the per-user mute preference.
          playRawr();
          // Track completed session to backend
          if (isAuthenticated && sessionDuration > 0) {
            const durationMin = Math.round(sessionDuration / 60 * 10) / 10;
            apiFetch('/api/subscription/track-timer', {
              method: 'POST',
              body: { duration_minutes: durationMin }
            }).catch(() => {});
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isRunning, timeLeft, isAuthenticated, sessionDuration, playRawr]);
  
  const handleStart = () => {
    const totalSeconds = (minutes * 60) + seconds;
    if (totalSeconds > 0) {
      setTimeLeft(totalSeconds);
      setSessionDuration(totalSeconds);
      setIsRunning(true);
      setShowEndMessages(false);
    }
  };
  
  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(0);
    setShowEndMessages(false);
    setMinutes(0);
    setSeconds(0);
    setLlamaPos(100);
  };
  
  const adjustTime = (type, amount) => {
    if (type === 'minutes') {
      setMinutes(prev => Math.max(0, Math.min(15, prev + amount)));
    } else {
      setSeconds(prev => {
        const newVal = prev + amount;
        if (newVal >= 60) return 45;
        if (newVal < 0) return 0;
        return newVal;
      });
    }
  };
  
  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <section className="py-8 sm:py-12 pb-32">
        <div className="container max-w-2xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 bg-gradient-to-r from-[#00E5CC] to-[#7C3AED] bg-clip-text text-transparent">
              {t('timerTitle')}
            </h1>
            <p className="text-muted-foreground">
              {isSpanish ? '¡Toma un descanso consciente con nuestro simpático dino y llama!' : 'Take a mindful break with our friendly dino and llama!'}
            </p>
          </div>
          
          {/* Game Display */}
          <Card className="mb-6 relative">
            {/* Floating "🔥 +1" reward — pops up briefly when a session completes */}
            {showXpFloat && (
              <div
                className="absolute top-2 right-4 z-20 pointer-events-none animate-rawr-pop text-2xl font-bold text-orange-500"
                data-testid="rawr-xp-float"
                aria-hidden="true"
              >
                🔥 +1
              </div>
            )}

            {/* Triceratops bonus toast — every 10 completed sessions */}
            {showTriceratopsToast && (
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 z-30 max-w-xs"
                data-testid="triceratops-toast"
              >
                <div className="rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500 text-white px-4 py-2 shadow-lg text-sm font-semibold flex items-center gap-2 animate-rawr-pop">
                  <span className="text-xl">🦕</span>
                  <span>
                    {isSpanish
                      ? `¡${sessionsToday} sesiones hoy! Aullido de Triceratops desbloqueado.`
                      : `${sessionsToday} sessions today! Triceratops growl unlocked.`}
                  </span>
                </div>
              </div>
            )}

            <CardContent className="p-6">
              <div className="relative w-full h-[200px] overflow-hidden bg-gradient-to-b from-blue-50 to-white rounded-xl border-2 border-gray-200">
                {/* Clouds */}
                <div className="absolute top-4 left-10 w-10 h-3 bg-white rounded-full opacity-70 shadow-sm"></div>
                <div className="absolute top-6 left-40 w-14 h-4 bg-white rounded-full opacity-60 shadow-sm"></div>
                <div className="absolute top-3 right-20 w-8 h-3 bg-white rounded-full opacity-70 shadow-sm"></div>
                
                {showEndMessages ? (
                  <div className="flex items-end justify-between px-4 h-full pb-12">
                    {/* Dino with speech bubble */}
                    <div className="relative">
                      <div className="absolute -top-24 left-0 bg-[#00E5CC] text-[#0d1f2d] text-sm p-3 rounded-xl shadow-lg z-10" style={{ width: '150px' }}>
                        {isSpanish ? '¡Gracias por tomar este tiempo con nosotros!' : 'Thanks for taking this time with us!'}
                        <div className="absolute -bottom-2 left-4 w-4 h-4 bg-[#00E5CC] transform rotate-45"></div>
                      </div>
                      <DinoSprite frame={0} isJumping={false} />
                    </div>
                    
                    {/* Llama with speech bubble */}
                    <div className="relative">
                      <div className="absolute -top-24 right-0 bg-[#7C3AED] text-white text-sm p-3 rounded-xl shadow-lg z-10" style={{ width: '150px' }}>
                        {isSpanish ? '¡Disfrutamos mucho tu compañía! 😁' : 'We really enjoyed your company 😁'}
                        <div className="absolute -bottom-2 right-4 w-4 h-4 bg-[#7C3AED] transform rotate-45"></div>
                      </div>
                      <LlamaSprite />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Dino */}
                    <div className="absolute bottom-10 left-12">
                      <DinoSprite frame={frame} isJumping={isJumping} />
                    </div>
                    
                    {/* Moving llama */}
                    <div 
                      className="absolute bottom-10"
                      style={{ left: `${llamaPos}%` }}
                    >
                      <LlamaSprite />
                    </div>
                  </>
                )}
                
                {/* Ground */}
                <div className="absolute bottom-8 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00E5CC] via-gray-400 to-[#7C3AED]"></div>
                
                {/* Ground texture */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-around">
                  {[...Array(15)].map((_, i) => (
                    <div key={i} className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Timer Display */}
          {isRunning && (
            <div className="text-center mb-6">
              <div className="text-5xl font-mono font-bold text-foreground">
                {formatTime(timeLeft)}
              </div>
              <p className="text-sm text-muted-foreground mt-2">{isSpanish ? 'Tiempo restante' : 'Time remaining'}</p>
            </div>
          )}
          
          {/* Timer Controls */}
          {!isRunning && !showEndMessages && (
            <Card className="mb-6">
              <CardHeader>
                {/* 3-column header
                    LEFT  — "Change to:" label + Slower↔Faster Llama toggle
                    MID   — Set Timer title (centered)
                    RIGHT — "Celebratory Completion Rawr!" label + Mute/Unmute toggle

                    Each toggle text alternates with the active state so
                    users see the action they'd be performing (e.g. if the
                    llama is currently slow, the button says "Faster Llama?"). */}
                <div className="flex items-start justify-between gap-2 flex-wrap sm:flex-nowrap">
                  {/* LEFT — Slower/Faster Llama toggle */}
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                      {isSpanish ? 'Cambiar a:' : 'Change to:'}
                    </span>
                    <button
                      type="button"
                      onClick={toggleSlowerLlama}
                      title={slowerLlama
                        ? (isSpanish ? 'Cambiar a llama rápida' : 'Switch to faster llama')
                        : (isSpanish ? 'Cambiar a llama lenta' : 'Switch to slower llama')}
                      aria-label="Toggle llama speed"
                      data-testid="timer-slower-llama-toggle"
                      aria-pressed={slowerLlama}
                      className="h-9 px-3 rounded-full flex items-center gap-1.5 text-sm font-bold transition-colors bg-[#8B5CF6]/10 text-[#7C3AED] dark:text-[#A78BFA] hover:bg-[#8B5CF6]/20"
                    >
                      <span aria-hidden="true">🐢</span>
                      <span className="whitespace-nowrap">
                        {slowerLlama
                          ? (isSpanish ? '¿Llama rápida?' : 'Faster Llama?')
                          : (isSpanish ? '¿Llama lenta?' : 'Slower Llama?')}
                      </span>
                    </button>
                    {sessionsToday > 0 && (
                      <span
                        className="text-[10px] text-muted-foreground tabular-nums mt-1"
                        data-testid="sessions-today-count"
                        title={isSpanish ? 'Sesiones hoy' : 'Sessions today'}
                      >
                        🔥 {sessionsToday} {isSpanish ? 'hoy' : 'today'}
                      </span>
                    )}
                  </div>

                  {/* MID — main title, centered */}
                  <CardTitle className="text-center text-lg flex-1 self-center min-w-[120px]">{t('setTimer')}</CardTitle>

                  {/* RIGHT — Mute/Unmute toggle */}
                  <div className="flex flex-col items-end min-w-0">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 text-right">
                      {isSpanish ? '¡Rugido al completar!' : 'Celebratory Completion Rawr!'}
                    </span>
                    <button
                      type="button"
                      onClick={toggleRawrMute}
                      title={rawrMuted
                        ? (isSpanish ? 'Activar rugido al finalizar' : 'Unmute rawr')
                        : (isSpanish ? 'Silenciar rugido al finalizar' : 'Mute rawr')}
                      aria-label="Toggle rawr sound"
                      aria-pressed={!rawrMuted}
                      data-testid="timer-rawr-toggle"
                      className="h-9 px-3 rounded-full flex items-center gap-1.5 text-sm font-bold transition-colors text-foreground hover:bg-muted"
                    >
                      <span className="whitespace-nowrap">
                        {rawrMuted
                          ? (isSpanish ? 'Activar' : 'Unmute')
                          : (isSpanish ? 'Silenciar' : 'Mute')}
                      </span>
                      {rawrMuted
                        ? <VolumeX className="h-4 w-4" />
                        : <Volume2 className="h-4 w-4 text-[#00A896]" />}
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-8">
                  {/* Minutes */}
                  <div className="flex flex-col items-center">
                    <span className="text-sm text-muted-foreground mb-2">{t('minutes')}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustTime('minutes', 1)}
                        disabled={minutes >= 15}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <input
                        type="number"
                        value={minutes}
                        onChange={(e) => setMinutes(Math.max(0, Math.min(15, parseInt(e.target.value) || 0)))}
                        className="w-16 text-center text-2xl font-mono font-bold border rounded-lg p-2 bg-background text-foreground"
                        min="0"
                        max="15"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustTime('minutes', -1)}
                        disabled={minutes <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <span className="text-3xl font-bold text-muted-foreground">:</span>
                  
                  {/* Seconds */}
                  <div className="flex flex-col items-center">
                    <span className="text-sm text-muted-foreground mb-2">{isSpanish ? 'Segundos' : 'Seconds'}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustTime('seconds', 15)}
                        disabled={seconds >= 45}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <input
                        type="number"
                        value={seconds}
                        onChange={(e) => setSeconds(Math.max(0, Math.min(45, parseInt(e.target.value) || 0)))}
                        className="w-16 text-center text-2xl font-mono font-bold border rounded-lg p-2 bg-background text-foreground"
                        min="0"
                        max="45"
                        step="15"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustTime('seconds', -15)}
                        disabled={seconds <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">{isSpanish ? '(incrementos de 15 seg)' : '(15 sec increments)'}</span>
                  </div>
                </div>
                
                {/* Quick presets */}
                <div className="flex justify-center gap-2 mt-6">
                  <Button variant="outline" size="sm" onClick={() => { setMinutes(1); setSeconds(0); }}>1 min</Button>
                  <Button variant="outline" size="sm" onClick={() => { setMinutes(5); setSeconds(0); }}>5 min</Button>
                  <Button variant="outline" size="sm" onClick={() => { setMinutes(10); setSeconds(0); }}>10 min</Button>
                  <Button variant="outline" size="sm" onClick={() => { setMinutes(15); setSeconds(0); }}>15 min</Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-center gap-4">
            {!isRunning && !showEndMessages && (
              <Button
                size="lg"
                onClick={handleStart}
                disabled={minutes === 0 && seconds === 0}
                className="bg-[#00E5CC] hover:bg-[#00C9B2] text-[#0d1f2d] px-8"
              >
                <Play className="h-5 w-5 mr-2" />
                {t('startTimer')}
              </Button>
            )}
            
            {isRunning && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => setIsRunning(false)}
              >
                <Pause className="h-5 w-5 mr-2" />
                {t('pauseTimer')}
              </Button>
            )}
            
            {(isRunning || showEndMessages || timeLeft > 0) && (
              <Button
                size="lg"
                variant="outline"
                onClick={handleReset}
              >
                <RotateCcw className="h-5 w-5 mr-2" />
                {t('resetTimer')}
              </Button>
            )}
          </div>
          
          {showEndMessages && (
            <p className="text-center text-muted-foreground mt-6">
              {isSpanish ? '¡Buen trabajo tomando un descanso! Siéntete libre de iniciar otro temporizador 😊' : 'Great job taking a break! Feel free to start another timer 😊'}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default DinoLlamaTimerPage;
